class CartDrawer extends HTMLElement {
    constructor() {
        super();
        this.renderContents = this.renderContents.bind(this);
    }

    connectedCallback() {
        if (this.dataset.onCartPage) {
            // We do not need the open-close drawer behaviour on the cart page because the cart is already visible
            return;
        }

        this.addEventListener('keyup', event => {
            if (event.code && event.code.toUpperCase() === 'ESCAPE') {
                this.close();
            }
        });
        this.addEventListener('click', event => {
            if (
                event.target.hasAttribute('data-drawer-overlay')
                || event.target.closest('.drawer__close')
                || event.target.classList.contains('drawer__close')
            ) {
                this.close();
            }
        });

        const cartLink = document.querySelector('#cart-icon-bubble');
        cartLink.addEventListener('click', event => {
            event.preventDefault();
            this.open(cartLink);
        });
        cartLink.addEventListener('keydown', event => {
            if (event.code && event.code.toUpperCase() === 'SPACE') {
                event.preventDefault();
                this.open(cartLink);
            }
        });
    }

    open(opener) {
        this.opener = opener;
        this.setAttribute('open', '');
        // here the animation doesn't seem to always get triggered. A timeout seem to help
        setTimeout(() => this.classList.add('animate', 'active'));
        this.focus();
        if (!this.dataset.ranMutators) {
            BryanAnthonys.cart.runMutators();
            this.dataset.ranMutators = 'true';
        }
        document.dispatchEvent(new CustomEvent('cartDrawerOpen'));
    }

    focus(trapInstantly = false) {
        const trapDelay = trapInstantly ? 0 : 300;

        setTimeout(() => {
            if (this.hasAttribute('open')) {
                trapFocus(this.getDialog());
            }
        }, trapDelay);

        document.body.classList.add('overflow-hidden');
    }

    close() {
        this.querySelectorAll('modal-dialog[open], [data-inherits-from-modal][open]')
            .forEach(modal => modal.hide('no animation'));
        this.removeAttribute('open');
        this.classList.remove('active');
        this.blur();
        document.dispatchEvent(new CustomEvent('cartDrawerClosed'));
    }

    blur() {
        removeTrapFocus(this.opener || document.querySelector('#cart-icon-bubble'));
        document.body.classList.remove('overflow-hidden');
    }

    renderContents(parsedState, opener, openDrawer = true) {
        if (parsedState.item_count === 0) {
            this.classList.add('is-empty');
        } else {
            this.classList.remove('is-empty');
        }

        this.getSectionsToRender().forEach((section => {
            const sectionElement = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
            // eslint-disable-next-line no-unsanitized/property
            sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }));

        if (openDrawer && !this.dataset.onCartPage) {
            setTimeout(() => this.open(opener || document.activeElement));
        }
        BryanAnthonys.cart.runMutators();
        this.dispatchEvent(new CustomEvent('cartRendered', {bubbles: true}));
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
        return new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
        const cartSectionName = this.dataset.onCartPage ? 'main-cart-page' : 'cart-drawer';

        return [
            {
                id: cartSectionName,
                selector: '#CartDrawer'
            },
            {
                id: 'cart-icon-bubble'
            }
        ];
    }

    getSectionIdsToRender() {
        return this.getSectionsToRender().map((section) => section.id);
    }

    getDialog() {
        return this.querySelector('[role="dialog"]');
    }
}

customElements.define('cart-drawer', CartDrawer);

customElements.define('cart-remove-button', class extends HTMLElement {
    connectedCallback() {
        this.addEventListener('click', event => this.removeItem(event));
        this.addEventListener('keyup', event => {
            if (event.code && event.code.toUpperCase() === 'SPACE') {
                this.removeItem(event);
            }
        });

        setTimeout(() => {
            const link = this.querySelector('a');
            link.setAttribute('role', 'button');
        }, 50);
    }

    removeItem(event) {
        event.preventDefault();
        const cartItems = this.closest('cart-items');

        if (this.dataset.crosslineSyncKey) {
            cartItems.updateQuantityBySyncKey(this.dataset.crosslineSyncKey, 0);
        } else {
            cartItems.updateQuantity(this.dataset.index, 0);
        }

        this.dispatchEvent(new CustomEvent('cartItemRemoved', {bubbles: true}));
    }
});

class CartItems extends HTMLElement {
    initialize() {
        this.lineItemStatusElement = document.getElementById('CartDrawer-LineItemStatus');

        this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
            .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

        this.debouncedOnChange = BryanAnthonys.debounce((event) => this.onChange(event), 600);
        this.addEventListener('change', this.debouncedOnChange.bind(this));

        this.cart = document.getElementById('CartDrawer');
    }

    onChange(event) {
        if (event.target.dataset.crosslineSyncKey) {
            this.updateQuantityBySyncKey(event.target.dataset.crosslineSyncKey, event.target.value);
        } else {
            this.updateQuantity(event.target.dataset.index, event.target.value);
        }
    }

    updateQuantity(line, quantity) {
        this.enableLoading();

        const body = JSON.stringify({
            line,
            quantity,
            sections: this.cart.getSectionIdsToRender(),
            sections_url: BryanAnthonys.pagePath()
        });

        BryanAnthonys.cart.change(body)
            .then(parsedState => {
                this.cart.renderContents(parsedState);
                this.updateLiveRegions(line, parsedState.item_count);
            })
            .catch(exception => this.handleQuantityUpdateError(exception))
            .finally(() => this.disableLoading());
    }

    updateQuantityBySyncKey(syncKey, quantity) {
        this.enableLoading();

        const updates = {};
        const inputs = this.querySelectorAll(`[name="updates[]"][data-crossline-sync-key="${syncKey}"]`);
        inputs.forEach(input => updates[input.dataset.cartItemKey] = quantity);

        const body = {
            updates,
            sections: this.cart.getSectionIdsToRender().join(','),
            sections_url: BryanAnthonys.pagePath()
        };

        BryanAnthonys.cart.update(JSON.stringify(body))
            .then(parsedState => {
                this.cart.renderContents(parsedState);
                this.currentItemCount = parsedState.item_count;
            })
            .catch(exception => this.handleQuantityUpdateError(exception))
            .finally(() => this.disableLoading());
    }

    handleQuantityUpdateError(exception) {
        this.querySelectorAll('.loading-overlay')
            .forEach(overlay => overlay.classList.add('hidden'));
        const errors = document.getElementById('CartDrawer-CartErrors');
        errors.textContent = BryanAnthonys.strings.cart.error;
        if (exception instanceof AjaxException && exception.message) {
            errors.textContent += ' ' + exception.message;
        }
        errors.scrollIntoView();
        console.error(exception);
    }

    updateLiveRegions(line, itemCount) {
        if (this.currentItemCount === itemCount) {
            const lineItemError = document.getElementById(`CartDrawer-LineItemError-${line}`);
            const quantityElement = document.getElementById(`Drawer-quantity-${line}`);
            if (lineItemError) {
                lineItemError.querySelector('.cart-item__error-text').innerHTML = BryanAnthonys.htmlReviewedForSafety(
                    BryanAnthonys.strings.cart.quantityError.replace('[quantity]', parseInt(quantityElement.value))
                );
                lineItemError.classList.add('mt-2');
            }
        }

        this.currentItemCount = itemCount;

        const cartStatus = document.getElementById('CartDrawer-LiveRegionText');
        cartStatus.setAttribute('aria-hidden', false);
        setTimeout(() => {
            cartStatus.setAttribute('aria-hidden', true);
        }, 1000);
    }

    getSectionInnerHTML(html, selector) {
        return new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector(selector).innerHTML;
    }

    enableLoading() {
        this.classList.add('cart__items--disabled');
        this.lineItemStatusElement.setAttribute('aria-hidden', false);
        document.activeElement.blur();
    }

    disableLoading() {
        this.classList.remove('cart__items--disabled');
        this.lineItemStatusElement.setAttribute('aria-hidden', true);
    }
}

customElements.define('cart-items', CartItems);

customElements.define('cart-note', class extends HTMLElement {
    connectedCallback() {
        this.addEventListener('click', event => {
            if (event.target.nodeName === 'BUTTON') {
                this.save(event.target);
            }
        });
    }

    textarea() {
        return this.querySelector('textarea');
    }

    save(button) {
        if (button.classList.contains('loading')) {
            return;
        }

        button.classList.add('loading');

        BryanAnthonys.cart.update(JSON.stringify({note: this.textarea().value}))
            .then(() => this.closest('modal-dialog').hide())
            .catch(() => {
                this.insertAdjacentHTML('beforeend', BryanAnthonys.htmlReviewedForSafety(`
                    <div class="status-banner status-banner--error mt-2">
                        ${BryanAnthonys.strings.general.genericAjaxError}
                    </div>
                `));
            })
            .finally(() => button.classList.remove('loading'));
    }
});

customElements.define('cart-quantity', class extends HTMLElement {
    connectedCallback() {
        this.addEventListener('click', () => {
            if (!this.classList.contains('product-option')) {
                return;
            }

            this.querySelector('.cart-drawer-item__quantity').classList.remove('hidden');
            this.querySelector('.cart-drawer-item__quantity-text').classList.add('hidden');
            this.classList.remove('product-option');
        });
    }
});

customElements.define('cart-product-message', class extends HTMLElement {
    initialize() {
        const contentBlocks = Array.from(this.querySelectorAll('.prodfit'))
            .filter(block => block.textContent.trim().length);

        if (contentBlocks.length > 1) {
            // eslint-disable-next-line no-unsanitized/property
            this.innerHTML = contentBlocks[0].innerHTML;
        }
    }
});

class QuickAddOpener extends HTMLElement {
    constructor() {
        super();

        this.modalParentSelector = 'body';
        this.modalAttributes = [];
        this.modalClasses = [];
        this.isEdit = false;
        this.product = null;
    }

    connectedCallback() {
        this.addEventListener('click', event => {
            if (event.target.nodeName === 'BUTTON' || event.target.closest('button')) {
                this.openModal();
            }
        });
    }

    openModal() {
        if (this.hasAttribute('data-open')) {
            return;
        }

        this.setAttribute('data-open', '');
        this.modalId = `QuickAdd-${this.dataset.productHandle}`;

        const html = `
            <quick-add-modal id="${this.modalId}" class="custom-modal quick-add-modal ${this.modalClasses.join(' ')}" ${this.modalAttributes.join(' ')}>
                <div 
                    class="custom-modal__content ${!this.isEdit ? 'custom-modal__content--quick-modal' : ''}" 
                    aria-label="${this.isEdit ? 'Edit product modal' : 'Add product modal'}" role="dialog" 
                    aria-modal="true" tabindex="-1" 
                >
                    <div class="custom-modal__content-info p-0">
                        <button
                            id="ModalClose-${this.modalId}" aria-label="Close modal" type="button"
                            class="custom-modal__toggle custom-modal__toggle--borderless custom-modal__toggle--corner custom-modal__toggle--small custom-modal__toggle--transparent"
                        >
                            <svg class="icon icon-close" aria-hidden="true" focusable="false"><use href="#icon-close"/></svg>
                        </button>
                        <div class="quick-add-modal-loading custom-modal__loading" data-modal-loading>
                            <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                                <circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>
                            </svg>
                        </div>
                    </div>
                </div>
            </quick-add-modal>
        `;

        document.querySelector(this.modalParentSelector).insertAdjacentHTML(
            'beforeend',
            BryanAnthonys.htmlReviewedForSafety(BryanAnthonys.partiallySanitize(html))
        );
        this.modal = document.getElementById(this.modalId);
        this.modal.show(this);

        BryanAnthonys.loadScript('storefront-catalog', {callback: () => this.modal.renderProductAsync(this.dataset.productHandle, this.dataset.variant)});
    }
}

customElements.define('cart-edit-button', class extends QuickAddOpener {
    constructor() {
        super();
        const cart = document.getElementById('CartDrawer');
        if (cart.dataset.onCartPage) {
            this.modalParentSelector = 'body';
            this.modalClasses = ['custom-modal--centered', 'custom-modal--animate-drop', 'custom-modal--cart-edit'];
            this.modalAttributes = ['data-animated="true"', 'data-animation-duration="400"', 'data-inherits-from-modal'];
        } else {
            this.modalParentSelector = '#CartDrawer';
            this.modalClasses = ['custom-modal--bottom-drawer', 'custom-modal--animate-rise'];
            this.modalAttributes = [
                'data-unmovable="true"', 'data-scope="CartDrawer"', 'data-animated="true"',
                'data-animation-duration="400"', 'data-inherits-from-modal'
            ];
        }
        this.isEdit = true;
    }
});

customElements.define('quick-add-button', class extends QuickAddOpener {
    constructor() {
        super();

        this.modalClasses = ['custom-modal--centered', 'custom-modal--animate-drop', 'quick-add-modal--body'];
        this.modalAttributes = ['data-animated="true"', 'data-animation-duration="300"', 'data-inherits-from-modal'];
    }
});

customElements.define('quick-add-modal', class extends ModalDialog {
    constructor() {
        super();
        document.querySelectorAll('quick-add-modal').forEach(modal => {
            if (modal !== this) {
                modal.remove();
            }
        });
        this.cart = document.getElementById('CartDrawer');

        this.addEventListener('swatchButtonsVariantChanged', this.handleSwatchChange.bind(this));
        this.addEventListener('submit', this.handleFormSubmit.bind(this));
    }

    resetModal() {
        const productContent = this.querySelector('[data-quick-product-content]');
        if (productContent) {
            productContent.remove();
        }

        const loader = this.querySelector('[data-modal-loading]');
        if (loader) {
            loader.classList.remove('hidden');
        }
    }

    renderProductAsync(handle, variantId = null) {
        this.resetModal();

        BryanAnthonys.getProduct(handle, 'full-json')
            .then(jsonProductData => {
                if (!this.isConnected) {
                    return;
                }

                const product = new JsonProduct(JSON.parse(JSON.stringify(jsonProductData.product)));

                this.render(product, variantId, jsonProductData.related_products_swatches);

                this.dispatchEvent(new CustomEvent('renderedProductInQuickAdd', {
                    detail: {
                        product: jsonProductData.product
                    },
                    bubbles: true
                }));
            });
    }

    render(product, variantId = null, relatedProductsSwatches = null) {
        this.product = product;
        const selectedVariant = variantId
            ? product.getVariants().find(variant => variant.getId() === parseInt(variantId))
            : product.firstAvailableVariant() || product.firstVariant();

        const swatchOptions = {
            tabFocusable: true,
            showActiveOption: true,
            showOptionName: true
        };

        const selectedQuantity = this.opener.dataset.quantity || 1;
        const buttonText = this.opener.isEdit
            ? BryanAnthonys.strings.general.save
            : BryanAnthonys.strings.variant.addToCart;

        let properties = '';
        if (this.opener.dataset.properties) {
            JSON.parse(atob(this.opener.dataset.properties)).forEach(property => {
                properties += `
                    <input 
                        type="hidden" name="properties[${BryanAnthonys.escapeHtml(property[0])}]" 
                        value="${BryanAnthonys.escapeHtml(String(property[1]))}"
                    />
                `;
            });
        }

        let relatedProductsSwatchesHtml = '';
        if (relatedProductsSwatches) {
            relatedProductsSwatchesHtml = `
                <related-products-swatches>
                    ${relatedProductsSwatches}
                    <cart-element-initializer></cart-element-initializer>
                </related-products-swatches>
            `;
        }

        let form = '';
        if (selectedVariant.getPrice() > 0) {
            form = `
                <form data-product-quick-form>
                    <input type="hidden" name="utf8" value="✓">
                    <input type="hidden" data-variant-id value="${selectedVariant.getId()}" name="id"/>
                    ${properties}
                    <div class="quick-product__swatches ${this.opener.dataset.optionsUneditable ? 'quick-product__swatches--uneditable' : ''}">
                        ${StorefrontCatalog.renderSwatches(product, selectedVariant, 'all', swatchOptions)}
                    </div>
                    <div data-selection-errors></div>
                    
                    ${relatedProductsSwatchesHtml}
                    
                    <quantity-input class="quantity mb-2">
                        <button class="quantity__button no-js-hidden" name="minus" type="button">
                            <span class="visually-hidden">
                                ${BryanAnthonys.strings.quantity.decrease.replace('{{ product }}', product.getTitle())}
                            </span>
                            -
                        </button>
                        <input class="quantity__input" type="number" data-quantity-input value="${selectedQuantity}" name="quantity"
                             aria-label="${BryanAnthonys.strings.quantity.inputLabel.replace('{{ product }}', product.getTitle())}">
                        <button class="quantity__button no-js-hidden" name="plus" type="button">
                            <span class="visually-hidden">
                                ${BryanAnthonys.strings.quantity.increase.replace('{{ product }}', product.getTitle())}
                            </span>
                            +
                        </button>
                    </quantity-input>
                    
                    <button type="submit" class="button button--small uppercase" data-add-button>
                        ${buttonText}
                    </button>
                    <div data-feedback aria-live="polite"></div>
                </form>
            `;
        }

        const html = `
            <div class="quick-product" data-quick-product-content>
                ${StorefrontCatalog.renderGallery(product, selectedVariant, {cssClasses: 'quick-product__gallery'})}
                <div class="quick-product__details p-4 pb-5">
                    <div class="size-14 uppercase letter-spacing-1 mb-2 semi-heavy-weight">
                        ${product.getTitle()}
                    </div>
                    ${StorefrontCatalog.renderPrice(product, selectedVariant, this.getPriceOptions())}
                    ${form}
                    <span class="visually-hidden" aria-live="polite" data-aria-announcements></span>
                </div>
            </div>
        `;

        const loader = this.querySelector('[data-modal-loading]');
        loader.insertAdjacentHTML('beforebegin',
            BryanAnthonys.htmlReviewedForSafety(BryanAnthonys.partiallySanitize(html))
        );

        loader.classList.add('hidden');
    }

    handleSwatchChange(event) {
        // TODO: make gallery panzoomable
        const variant = event.detail.variant;

        const input = this.querySelector('input[data-variant-id]');
        input.value = variant ? variant.id : '';

        const announcements = this.querySelector('[data-aria-announcements]');
        if (announcements && variant) {
            let text = `Changed variant to ${variant.title}`;
            if (!variant.available) {
                text = `${text} - ${BryanAnthonys.strings.variant.soldOut}`;
            }
            announcements.textContent = text;
        }

        const formButton = this.querySelector('button[type="submit"]');

        const selectionErrors = this.querySelector('[data-selection-errors]');
        if (variant && variant.available) {
            selectionErrors.innerHTML = '';
            formButton.removeAttribute('disabled');
        } else {
            const text = variant ? BryanAnthonys.strings.variant.soldOut : BryanAnthonys.strings.variant.unavailable;
            selectionErrors.innerHTML = `<span class="error" data-error-message></span>`;
            selectionErrors.querySelector('[data-error-message]').textContent = text;
            formButton.setAttribute('disabled', 'true');
        }

        if (variant && variant.featured_image) {
            const firstSliderImage = this.querySelector('.quick-product__gallery .slider__slide img');
            if (firstSliderImage) {
                firstSliderImage.src = variant.featured_image.src;
            }
        }

        if (variant && this.product) {
            const selectedVariant = this.product.getVariants().find(jsonVariant => jsonVariant.getId() === parseInt(variant.id));
            const newPrice = StorefrontCatalog.renderPrice(this.product, selectedVariant, this.getPriceOptions());
            this.querySelectorAll('[data-price]').forEach(price => {
                price.insertAdjacentHTML(
                    'beforebegin',
                    BryanAnthonys.htmlReviewedForSafety(BryanAnthonys.partiallySanitize(newPrice))
                );
                price.remove();
            });
        }
    }

    getPriceOptions() {
        const priceOptions = {priceClasses: ['mb-3', 'size-13']};
        if (this.opener.dataset.discountPercentage) {
            priceOptions.discount = parseInt(this.opener.dataset.discountPercentage) || 0;
        }

        return priceOptions;
    }

    handleFormSubmit(event) {
        event.preventDefault();
        const button = this.querySelector('button[type="submit"]');
        if (button.classList.contains('loading')) {
            return;
        }

        this.querySelector('[data-feedback]').innerHTML = '';
        button.classList.add('loading');
        button.setAttribute('aria-disabled', 'true');
        if (this.opener.isEdit) {
            this.saveEdit(event.target, button);
        } else {
            this.addToCart(event.target, button);
        }
    }

    saveEdit(form, button) {
        const formData = new FormData(form);
        const cartItems = this.cart.querySelector('cart-items');
        const currentVariant = parseInt(this.opener.dataset.variant);
        const currentQuantity = parseInt(this.opener.dataset.quantity);
        const newVariant = parseInt(formData.get('id'));
        const newQuantity = parseInt(formData.get('quantity'));

        const changeLineQuantity = (key, quantity) => {
            BryanAnthonys.cart.fetch()
                .then(cart => {
                    cartItems.currentItemCount = cart.item_count;
                    let lineNumber = null;
                    cart.items.forEach((item, index) => {
                        if (item.key === key) {
                            lineNumber = index + 1;
                        }
                    });

                    if (lineNumber !== null) {
                        cartItems.updateQuantity(lineNumber, quantity);

                        button.classList.remove('loading');
                        this.hide();
                    } else {
                        // Our cart is outdated and the item we try to edit no longer exists
                        window.location.reload();
                    }
                })
                .catch(() => window.location.reload());
        };

        if (currentVariant === newVariant) {
            if (currentQuantity !== newQuantity) {
                changeLineQuantity(this.opener.dataset.key, newQuantity);
            } else {
                button.classList.remove('loading');
                this.hide();
            }
        } else {
            BryanAnthonys.cart.add(formData, 'cart')
                .then(() => changeLineQuantity(this.opener.dataset.key, 0))
                .catch(exception => {
                    button.classList.remove('loading');
                    button.removeAttribute('aria-disabled');
                    this.handleCartOperationException(exception);
                });
        }
    }

    addToCart(form, button) {
        const formData = new FormData(form);
        formData.append('sections', this.cart.getSectionIdsToRender().join(','));
        formData.append('sections_url', BryanAnthonys.pagePath());

        const cartRelated = this.closest('cart-drawer')
            || window.location.href.includes('/cart')
            || this.dataset.cartRelated;

        BryanAnthonys.cart.add(formData, cartRelated ? 'cart' : 'quick-add')
            .then(response => {
                this.cart.renderContents(response);
                this.hide('no animation');
                if (!this.closest('cart-drawer') && !this.cart.hasAttribute('open')) {
                    document.getElementById('cart-icon-bubble').click();
                }
            })
            .catch(exception => this.handleCartOperationException(exception))
            .finally(() => {
                button.classList.remove('loading');
                button.removeAttribute('aria-disabled');
            });
    }

    handleCartOperationException(exception) {
        if (exception instanceof AjaxException) {
            this.querySelector('[data-feedback]').innerHTML = `
                <div class="status-banner status-banner--error mt-2">
                    ${BryanAnthonys.escapeHtml(exception.description)}
                </div>
            `;
        } else {
            console.error(exception);
        }
    }

    hide(forceClose = false) {
        super.hide(forceClose);

        this.opener.removeAttribute('data-open');
        if (!this.hasAttribute('open')) {
            setTimeout(() => this.remove(), 500);
        }
    }

    blur() {
        removeTrapFocus(this.opener.querySelector('button'));
        document.body.classList.remove('overflow-hidden');
    }
});

customElements.define('related-products-swatches', class extends HTMLElement {
    initialize() {
        this.modal = this.closest('quick-add-modal');

        const relatedProductslinks = this.querySelectorAll('a');
        relatedProductslinks.forEach(relatedProductLink => {
            relatedProductLink.addEventListener('click', (e) => {
                e.preventDefault();

                this.modal.renderProductAsync(BryanAnthonys.getProductHandleFromUrl(relatedProductLink.getAttribute('href')));
            });
        });

        const urlSelector = this.querySelector('url-selector');
        if (urlSelector) {
            urlSelector.setChangeCallback((url) => {
                this.modal.renderProductAsync(BryanAnthonys.getProductHandleFromUrl(url));

                return true;
            });
        }
    }
});

customElements.define('cross-sell-add', class extends HTMLElement {
    connectedCallback() {
        this.onHover = this.onHover.bind(this);
        this.addEventListener('click', event => {
            const button = event.target.nodeName.toLowerCase() === 'button'
                ? event.target
                : event.target.closest('button');

            if (!button || button.classList.contains('loading')) {
                return;
            }

            button.classList.add('loading');

            const cart = document.querySelector('cart-drawer');
            const formData = new FormData();
            formData.append('id', this.dataset.variantId);
            formData.append('sections_url', BryanAnthonys.pagePath());
            formData.append('sections', cart.getSectionIdsToRender());
            if (this.dataset.limitedToOne) {
                formData.append('properties[_one_per_cart]', 'Yes');
            }
            if (this.dataset.specialPriceProp) {
                formData.append('properties[_Special offer]', 'Yes');
            }
            formData.append('properties[_cross_sell]', 'Yes');

            BryanAnthonys.cart.add(formData, 'cross-sells')
                .then(response => cart.renderContents(response))
                .catch(error => {
                    console.error(error);
                    BryanAnthonys.showToast(BryanAnthonys.strings.cart.error, 'error');
                })
                .finally(() => button.classList.remove('loading'));
        });

        this.addEventListener('mouseenter', this.onHover);
    }

    onHover() {
        const crossSellsSlideshow = this.closest('cross-sells');
        if (crossSellsSlideshow) {
            crossSellsSlideshow.pauseOnInteraction();
            this.removeEventListener('mouseenter', this.onHover);
        }
    }
});

customElements.define('cross-sell-info-modal-opener', class extends HTMLElement {
    connectedCallback() {
        this.addEventListener('click', () => this.renderModal());
    }

    renderModal() {
        const cart = document.getElementById('CartDrawer');
        cart.querySelectorAll('cross-sell-info-modal').forEach(modal => modal.remove());


        let modalScopeAttribute = '';
        if (this.dataset.modalScope) {
            modalScopeAttribute = `data-scope="${this.dataset.modalScope}"`;
        } else if (!cart.dataset.onCartPage) {
            modalScopeAttribute = 'data-scope="CartDrawer"';
        }

        cart.insertAdjacentHTML('beforeend', BryanAnthonys.htmlReviewedForSafety(`
                <cross-sell-info-modal
                    id="CrossSellModal" data-animated="true" data-animation-duration="400"
                    class="custom-modal custom-modal--centered custom-modal--animate-drop custom-modal--cross-sell"
                    ${modalScopeAttribute} data-unmovable="true"
                    data-handle="${this.dataset.handle}"
                    data-content-url="${this.dataset.contentUrl ? this.dataset.contentUrl : '/?section_id=cross-sell-details'}"
                    data-inherits-from-modal
                >
                    <div
                        class="custom-modal__content" role="dialog" tabindex="-1"
                        aria-label="Cart add-on details" style="--max-width: 45rem;" aria-modal="true" 
                    >
                        <div class="custom-modal__content-info center">
                            <button
                                id="ModalClose-CrossSellModal" aria-label="Close modal" type="button"
                                class="custom-modal__toggle custom-modal__toggle--borderless custom-modal__toggle--corner custom-modal__toggle--small"
                            >
                                <svg class="icon icon-close" aria-hidden="true" focusable="false">
                                    <use href="#icon-close"/>
                                </svg>
                            </button>
                            <div class="custom-modal__loading" data-modal-loading>
                                <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                                    <circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>
                                </svg>
                            </div>
                        </div>
                    </div>
                </cross-sell-info-modal>
            `));
        document.getElementById('CrossSellModal').show(this);
    }
});

customElements.define('cross-sell-info-modal', class extends ModalDialog {
    connectedCallback() {
        return super.connectedCallback();
    }

    renderAjaxContent(html) {
        super.renderAjaxContent(html);
        const crossSellGroup = this.querySelector(`[data-handle="${this.dataset.handle}"]`);
        crossSellGroup.classList.remove('hidden');
        this.prepareButton(crossSellGroup);
    }

    prepareButton(crossSellGroup) {
        const button = crossSellGroup.querySelector('[data-interaction-button]');
        if (!button) {
            return;
        }
        const crossSellHandle = button.dataset.handle;
        const inCartItem = BryanAnthonys.cart.getCache().items
            .find(item => item.handle === crossSellHandle);

        if (inCartItem) {
            button.querySelector('[data-text-remove]').classList.remove('hidden');
        } else {
            button.querySelector('[data-text-add]').classList.remove('hidden');
        }

        button.classList.add('mt-4');

        button.addEventListener('click', () => {
            if (button.disabled) {
                return;
            }

            button.disabled = true;
            const target = inCartItem
                ? document.querySelector(`cart-remove-button a[data-product-handle="${crossSellHandle}"]`)
                : document.querySelector(
                    button.dataset.addSelector ? button.dataset.addSelector
                        : `cross-sell-add[data-handle="${crossSellHandle}"] button`
                );

            if (target) {
                this.hide('force close');
                target.click();
            }
        });
    }
});

customElements.define('cross-sell-products-modal-opener', class extends HTMLElement {
    connectedCallback() {
        this.addEventListener('click', () => this.renderModal());
        this.addEventListener('mouseenter', () => this.onHover());
    }

    renderModal() {
        const cart = document.getElementById('CartDrawer');
        cart.querySelectorAll('#CrossSellProductsModal').forEach(modal => modal.remove());
        const url = this.prepareUrl();
        const modalClasses = this.dataset.type === 'recommendations'
            ? 'custom-modal--bottom-drawer custom-modal--animate-rise'
            : 'custom-modal--centered custom-modal--animate-drop custom-modal--cross-sell-collection';

        cart.insertAdjacentHTML('beforeend', BryanAnthonys.htmlReviewedForSafety(BryanAnthonys.partiallySanitize(`
                <modal-dialog
                    id="CrossSellProductsModal" data-animated="true" data-animation-duration="400"
                    class="custom-modal ${modalClasses} custom-modal--cross-sell-products"
                    ${!cart.dataset.onCartPage ? 'data-unmovable="true" data-scope="CartDrawer"' : ''}
                    data-content-url="${url}" data-inherits-from-modal
                >
                    <div
                        class="custom-modal__content" role="dialog" tabindex="-1"
                        aria-label="Cart add-ons" aria-modal="true" 
                    >
                        <div class="custom-modal__content-info center">
                            <button
                                id="ModalClose-CrossSellProductsModal" aria-label="Close modal" type="button"
                                class="custom-modal__toggle custom-modal__toggle--borderless custom-modal__toggle--corner custom-modal__toggle--small"
                            >
                                <svg class="icon icon-close" aria-hidden="true" focusable="false">
                                    <use href="#icon-close"/>
                                </svg>
                            </button>
                            <div class="custom-modal__loading" data-modal-loading>
                                <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                                    <circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>
                                </svg>
                            </div>
                        </div>
                    </div>
                </modal-dialog>
            `)));

        document.getElementById('CrossSellProductsModal').show(this);
    }

    prepareUrl() {
        if (this.dataset.type === 'recommendations') {
            return `/recommendations/products?product_id=${parseInt(this.dataset.productId)}&section_id=cross-sell-recommendations&limit=20`;
        } else {
            return `/collections/${this.dataset.collectionHandle}?section_id=cross-sell-collection`;
        }
    }

    onHover() {
        const crossSellsSlideshow = this.closest('cross-sells');
        if (crossSellsSlideshow) {
            crossSellsSlideshow.pauseOnInteraction();
            this.removeEventListener('mouseenter', this.onHover);
        }
    }
});

customElements.define('cross-sell-products', class extends HTMLElement {
    connectedCallback() {
        this.render();
    }

    render() {
        const url = this.prepareUrl();

        this.fetchAjaxContent(url)
            .then(html => this.renderAjaxContent(html))
            .catch(error => {
                this.renderAjaxError(error);
            });
    }

    prepareUrl() {
        if (this.dataset.type === 'recommendations') {
            return `/recommendations/products?product_id=${parseInt(this.dataset.productId)}&section_id=cross-sell-products&limit=20`;
        } else {
            return `/collections/${this.dataset.collectionHandle}?section_id=cross-sell-products`;
        }
    }

    fetchAjaxContent(url) {
        return new Promise(function (resolve, reject) {
            fetch(url)
                .then(BryanAnthonys.decodeTextResponse)
                .then(html => {
                    resolve(html);
                })
                .catch(reject);
        });
    }

    renderAjaxContent(html) {
        if (html.includes('product-card')) {
            this.classList.remove('hidden');

            this.insertAdjacentHTML('afterbegin', BryanAnthonys.htmlReviewedForSafety(BryanAnthonys.partiallySanitize(html)));

            this.style.height = `${this.scrollHeight}px`;
        }
    }

    renderAjaxError(error) {
        console.error(error);
        this.classList.add('hidden');
    }
});

customElements.define('cross-sell-collection-product-slide', class extends HTMLElement {
    initialize() {
        if (!this.dataset.variantId) {
            return;
        }

        const cardFooter = this.querySelector('.card-footer');
        if (!cardFooter) {
            return;
        }

        cardFooter.insertAdjacentHTML('beforeend', BryanAnthonys.htmlReviewedForSafety(`
            <cross-sell-add class="cross-sell__add" data-variant-id="${parseInt(this.dataset.variantId)}">
                <button type="button" class="button button--tiny uppercase letter-spacing-1 pl-2 pr-2 mt-2 mb-2">
                  ${BryanAnthonys.strings.variant.addToCart}
                </button>
            </cross-sell-add>
        `));
    }
});

customElements.define('cross-sell-product-card', class extends HTMLElement {
    initialize() {
        const modalOpener = this.querySelector('quick-add-button');
        if (!modalOpener) {
            return;
        }

        modalOpener.modalParentSelector = '#CartDrawer';
        modalOpener.modalClasses = ['custom-modal--bottom-drawer', 'custom-modal--animate-rise'];
        modalOpener.modalAttributes = [
            'data-unmovable="true"', 'data-scope="CrossSellProductsModal"', 'data-animated="true"',
            'data-animation-duration="400"', 'data-inherits-from-modal'
        ];
        if (this.dataset.properties) {
            modalOpener.dataset.properties = this.dataset.properties;
        }

        const button = modalOpener.querySelector('button');
        button.className = button.className.toString().replace(
            'button--tiny',
            'button--small uppercase letter-spacing-1'
        );

        const discount = parseInt(this.dataset.discountPercentage);
        if (discount > 0) {
            this.addDiscount(modalOpener, discount);
        }
    }

    addDiscount(modalOpener, discount) {
        const price = this.querySelector('[data-price]');
        if (price.dataset.onSale) {
            return;
        }

        const comparePriceCents = parseInt(price.dataset.productSalePriceCents);
        if (!comparePriceCents) {
            return;
        }
        const salePriceCents = Math.round(comparePriceCents - (comparePriceCents * discount / 100));

        price.classList.add('price--on-sale');
        price.querySelectorAll('[data-price-item-regular]').forEach(comparePrice => {
            comparePrice.textContent = BryanAnthonys.formatMoney(comparePriceCents);
        });
        price.querySelectorAll('[data-price-item-sale]').forEach(salePrice => {
            salePrice.textContent = BryanAnthonys.formatMoney(salePriceCents);
        });

        modalOpener.dataset.discountPercentage = discount;
    }
});

customElements.define('cross-sell-product-card-new', class extends HTMLElement {
    initialize() {
        const directAddButton = this.querySelector('direct-add-button');
        const modalOpener = this.querySelector('quick-add-button');
        if (!directAddButton && !modalOpener) {
            return;
        }

        this.addEventListener('swatchButtonsVariantChanged', event => this.handleVariantChange(event));

        if (directAddButton && this.dataset.properties) {
            directAddButton.dataset.properties = this.dataset.properties;
        }

        if (modalOpener) {
            modalOpener.modalParentSelector = '#CartDrawer';
            modalOpener.modalClasses = ['custom-modal--bottom-drawer', 'custom-modal--animate-rise'];
            modalOpener.modalAttributes = [
                'data-unmovable="true"', 'data-scope="CrossSellProductsModal"', 'data-animated="true"',
                'data-animation-duration="400"', 'data-inherits-from-modal'
            ];

            if (this.dataset.properties) {
                modalOpener.dataset.properties = this.dataset.properties;
            }

            this.modalOpener = modalOpener;
        }

        this.addDiscount();
    }

    handleVariantChange(event) {
        const variant = event.detail.variant;
        if (variant) {
            if (this.modalOpener) {
                this.modalOpener.dataset.variant = variant.id;
            }

            const imageInput = this.querySelector(`[data-image-variant-id="${variant.id}"]`);
            if (imageInput) {
                this.querySelector('[data-image]').src = imageInput.value;
            }

            const price = this.querySelector('[data-price]');
            price.dataset.productSalePriceCents = variant.price;

            const priceRegularElement = this.querySelector('.price__regular .price-item');
            if (priceRegularElement) {
                priceRegularElement.innerText = BryanAnthonys.formatMoney(variant.price, true);
            }
            const priceSpecialElement = this.querySelector('.price__sale .price-item--sale');
            if (priceSpecialElement) {
                priceSpecialElement.innerText = BryanAnthonys.formatMoney(variant.price, true);
            }
            const priceCompareElement = this.querySelector('.price__sale .price-item--regular');
            if (priceCompareElement) {
                const priceElement = priceCompareElement.closest('.price');
                if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                    priceCompareElement.innerText = BryanAnthonys.formatMoney(variant.compare_at_price, true);
                    priceElement.classList.add('price--on-sale');
                } else {
                    priceCompareElement.innerText = '';
                    priceElement.classList.remove('price--on-sale');
                }
            }

            this.addDiscount();
        }
    }

    addDiscount() {
        const discount = parseInt(this.dataset.discountPercentage);
        if (discount > 0) {
            const price = this.querySelector('[data-price]');
            if (price.dataset.onSale) {
                return;
            }
            const comparePriceCents = parseInt(price.dataset.productSalePriceCents);
            if (!comparePriceCents) {
                return;
            }
            const salePriceCents = Math.round(comparePriceCents - (comparePriceCents * discount / 100));

            price.classList.add('price--on-sale');
            price.querySelectorAll('[data-price-item-regular]').forEach(comparePrice => {
                comparePrice.textContent = BryanAnthonys.formatMoney(comparePriceCents);
            });
            price.querySelectorAll('[data-price-item-sale]').forEach(salePrice => {
                salePrice.textContent = BryanAnthonys.formatMoney(salePriceCents);
            });

            if (this.modalOpener) {
                this.modalOpener.dataset.discountPercentage = discount;
            }
        }
    }
});

customElements.define('direct-add-button', class extends HTMLElement {
    connectedCallback() {
        this.addEventListener('click', event => this.handleClick(event));
        this.productCard = this.closest('product-card');
        if (!this.productCard) {
            this.productCard = this.closest('[data-product-card]');
        }
        if (this.productCard) {
            this.productCard.addEventListener('swatchButtonsVariantChanged', this.handleSwatchChange.bind(this));
        }
    }

    handleClick(event) {
        const button = event.target.nodeName.toUpperCase() === 'BUTTON'
            ? event.target
            : event.target.closest('button');

        if (!button || button.classList.contains('loading')) {
            return;
        }

        button.classList.add('loading');

        const cart = document.querySelector('cart-drawer');
        const formData = new FormData();
        formData.append('id', this.dataset.variantId);
        formData.append('sections_url', BryanAnthonys.pagePath());
        formData.append('sections', cart.getSectionIdsToRender());

        if (this.dataset.properties) {
            JSON.parse(atob(this.dataset.properties)).forEach(property => {
                formData.append(`properties[${BryanAnthonys.escapeHtml(property[0])}]`, BryanAnthonys.escapeHtml(String(property[1])));
            });
        }

        BryanAnthonys.cart.add(formData, 'direct-add')
            .then(response => {
                cart.renderContents(response, button, !cart.hasAttribute('open'));
                button.innerText = button.dataset.addedText;
                button.insertAdjacentHTML(`afterbegin`, `
                    <svg class="icon icon-check" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 47 32" fill="none">
                      <path d="M1 17.0345L15.8046 31L46 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>
                `);
            })
            .catch(error => {
                console.error(error);
                BryanAnthonys.showToast(BryanAnthonys.strings.cart.error, 'error');
            })
            .finally(() => button.classList.remove('loading'));
    }

    handleSwatchChange(event) {
        const button = this.querySelector('button');
        const variant = event.detail.variant;
        if (!variant || !variant.available) {
            this.dataset.variantId = '';
            button.disabled = true;

            return;
        }

        this.dataset.variantId = variant.id;
        button.disabled = false;
    }
});

// This must remain as the last element in this file. Add new code above this.
// This element can only be used inside elements defined in this file.
customElements.define('cart-element-initializer', class extends HTMLElement {
    connectedCallback() {
        if (typeof this.parentElement.initialize === 'function') {
            this.parentElement.initialize();
        }
    }
});